import type {
  RouterInput,
  TrpcQueryRoutes,
  TrpcRouter,
  inferTrpcRouteOutput,
} from '@vrplatform/trpc-api';
import { showErrorNotification } from '@vrplatform/theme';
import {
  updateErrorNotification,
  updateSuccessNotification,
} from '@vrplatform/theme';
import { readSessionStorageValue } from '@mantine/hooks';
import { showNotification } from '@vrplatform/theme';
import {
  keepPreviousData,
  skipToken,
  type QueryClient,
} from '@tanstack/react-query';
import { httpBatchLink, TRPCClientError } from '@trpc/client';
import { createTRPCReact } from '@trpc/react-query';
import { useCallback, useEffect, useId } from 'react';
import { type PropsWithChildren, useMemo } from 'react';
import { PLATFORM } from '../env';
import {
  type QueryKey,
  type QueryKeyUnion,
  useInvalidateHasuraQueries,
} from '../graphql';
import { useSessionTokenProvider } from '../hooks/team/team.session';

export const trpc = createTRPCReact<TrpcRouter>();

const useTrpcClient = () => {
  const { getToken } = useSessionTokenProvider();

  return useMemo(() => {
    const getHeaders = async () => {
      const accessToken = await getToken();

      const replaySession = readSessionStorageValue<
        { id?: string } | undefined
      >({
        key: 'sentryReplaySession',
        defaultValue: undefined,
      });

      const headers: { [s: string]: string } = {
        authorization: `Bearer ${accessToken}`,
        'Finalytic-Platform': PLATFORM,
        platform: PLATFORM,
        'x-transaction-id': Math.random().toString(36).substring(2, 9),
      };

      if (replaySession?.id) headers['x-sentry-replay-id'] = replaySession.id;

      return headers;
    };

    return trpc.createClient({
      links: [
        httpBatchLink({
          url: '/trpc',
          headers: getHeaders,
        }),
      ],
    });
  }, [getToken]);
};

export const TrpcProvider = ({
  queryClient,
  children,
}: PropsWithChildren<{ queryClient: QueryClient }>) => {
  const trpcClient = useTrpcClient();

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      {children}
    </trpc.Provider>
  );
};

export const ExtensionTrpcProvider = ({
  queryClient,
  children,
  token,
}: PropsWithChildren<{
  queryClient: QueryClient;
  token: string;
}>) => {
  return (
    <DirectTokenTrpcProvider accessToken={token} queryClient={queryClient}>
      {children}
    </DirectTokenTrpcProvider>
  );
};

const getDirectTokenTrpcUrl = () => {
  return (
    import.meta.env.VITE_TRPC_URL ||
    (import.meta.env.DEV
      ? 'http://localhost:4001'
      : 'https://portal.vrplatform.app/trpc')
  );
};

export const DirectTokenTrpcProvider = ({
  accessToken,
  children,
  queryClient,
  url = getDirectTokenTrpcUrl(),
}: PropsWithChildren<{
  accessToken: string;
  queryClient: QueryClient;
  /** Defaults to the portal endpoint for clients outside the portal origin. */
  url?: string;
}>) => {
  const client = useMemo(() => {
    const getHeaders = () => {
      const headers: { [s: string]: string } = {
        authorization: `Bearer ${accessToken}`,
        'Finalytic-Platform': PLATFORM,
        platform: PLATFORM,
        'x-transaction-id': Math.random().toString(36).substring(2, 9),
      };

      return headers;
    };

    return trpc.createClient({
      links: [
        httpBatchLink({
          url,
          headers: getHeaders,
        }),
      ],
    });
  }, [accessToken, url]);

  return (
    <trpc.Provider client={client} queryClient={queryClient}>
      {children}
    </trpc.Provider>
  );
};

type Options<TTrpcRouteType extends keyof RouterInput> = {
  notificationId?: string;
  successMessage?: {
    title?: string | ((date: inferTrpcRouteOutput<TTrpcRouteType>) => string);
    message: string | ((date: inferTrpcRouteOutput<TTrpcRouteType>) => string);
  };
  errorMessage?: {
    title?: string | ((err: Error) => string | undefined);
    message?: string | ((err: Error) => string | undefined);
  };
  hideErrorNotification?: boolean;
};

type UseQueryOptionsFor<TPath extends TrpcQueryRoutes> = Parameters<
  (typeof trpc)[TPath]['useQuery']
>[1];

type UseQueryHookFor<TPath extends TrpcQueryRoutes> = (
  input: RouterInput[TPath] | typeof skipToken,
  opts: UseQueryOptionsFor<TPath>
) => ReturnType<(typeof trpc)[TPath]['useQuery']>;

export const useTrpcQuery = <TType extends TrpcQueryRoutes>(
  type: TType,
  args: RouterInput[TType],
  options: Options<TType> & {
    skip?: boolean;
    staleTime?: number;
    refetchOnWindowFocus?: boolean;
    queryKey?: QueryKey;
    keepPreviousData?: boolean;
  } = {}
) => {
  const queryKeys = Array.isArray(options?.queryKey)
    ? options?.queryKey || []
    : options?.queryKey
      ? [options.queryKey]
      : [];

  const invalidate = useInvalidateHasuraQueries(queryKeys);
  const method = trpc[type];

  const enabled = !options?.skip;

  const { refetchOnWindowFocus, keepPreviousData: shouldKeepPreviousData } =
    options;

  const useQueryForRoute = method.useQuery as UseQueryHookFor<TType>;

  const queryData = useQueryForRoute(enabled ? args : skipToken, {
    trpc: {
      abortOnUnmount: true,
    },
    refetchOnWindowFocus,
    staleTime: options.staleTime,
    placeholderData: shouldKeepPreviousData ? keepPreviousData : undefined,
  });

  const {
    data,
    isFetching: loading,
    refetch,
    isSuccess,
    isError,
    error,
    isInitialLoading,
  } = queryData;

  const handleError = useCallback(
    (err: unknown) => {
      const error =
        err instanceof TRPCClientError
          ? err
          : TRPCClientError.from<TrpcRouter>(
              (err as Error | object) ?? new Error('Unknown error')
            );

      if (window?.location?.host === 'localhost:4009') return;

      const getTitle = (
        type: 'title' | 'message',
        defaultValue: string | undefined
      ) => {
        const custom = options?.errorMessage?.[type];
        if (typeof custom === 'function') {
          return custom(error) ?? defaultValue;
        }
        return custom ?? defaultValue;
      };

      const lower = error?.message?.toLowerCase() || '';

      const disableNotification =
        lower.includes('failed to fetch') ||
        lower.includes('load failed') ||
        lower.includes('database query error') ||
        lower.includes('unauthorized');

      if (!options?.hideErrorNotification && !disableNotification) {
        showErrorNotification({
          title: getTitle(
            'title',
            `Query Error: ${error.data?.httpStatus} ${error.data?.code}`
          ),
          message: getTitle('message', error.message),
        });
      }
      invalidate();
    },
    [invalidate, options?.errorMessage, options?.hideErrorNotification]
  );

  useEffect(() => {
    if (isSuccess) {
      invalidate();
    }
  }, [isSuccess, invalidate]);

  useEffect(() => {
    if (isError) {
      handleError(error);
    }
  }, [isError, handleError, error]);

  return {
    data: data as inferTrpcRouteOutput<TType> | undefined,
    loading,
    refetch,
    isInitialLoading,
    error: error ?? undefined,
  };
};

export type { TRPCClientError };

export const useTrpcMutation = <TType extends keyof RouterInput>(
  type: TType,
  options: Options<TType> & {
    invalidateQueryKeys?: (QueryKeyUnion | (string & {}))[];
  } = {}
) => {
  const notificationId = useId();
  const notifyId = options?.notificationId || notificationId;

  const showSuccessNotify =
    !!options?.successMessage?.message || !!options?.notificationId;

  const invalidate = useInvalidateHasuraQueries(options.invalidateQueryKeys);

  const method = trpc[type] as any;
  const {
    mutateAsync,
    isPending: loading,
    error,
  } = method.useMutation({
    onSuccess: (data: inferTrpcRouteOutput<TType>) => {
      if (options?.successMessage?.message) {
        const message =
          typeof options.successMessage?.message === 'function'
            ? options.successMessage.message(data)
            : options.successMessage?.message;
        const title =
          typeof options.successMessage?.title === 'function'
            ? options.successMessage.title(data)
            : options.successMessage?.title;

        updateSuccessNotification({
          id: notifyId,
          title: title || 'Success!',
          message: message || 'Successfully updated your action.',
        });
      }
    },
    onError: (error: any) => {
      const getTitle = (
        type: 'title' | 'message',
        defaultValue: string | undefined
      ) => {
        if (typeof options?.errorMessage?.[type] === 'function')
          return (options.errorMessage?.[type] as any)?.(error) || defaultValue;
        return options?.errorMessage?.[type] || defaultValue;
      };

      let title = getTitle(
        'title',
        `Mutation Error: ${error.data?.httpStatus || 500} ${error.data?.code || 'Unknown'}`
      );
      let message = getTitle('message', error.message);

      const isRouteMissing = message.includes(
        `Unexpected token '<', "<!DOCTYPE "... is not valid JSON`
      );

      if (isRouteMissing) {
        // we need to refresh service worker
        title = 'Update Available';
        message = 'We are refreshing the page to apply the latest updates.';

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }

      if (showSuccessNotify) {
        updateErrorNotification({
          id: notifyId,
          title,
          message,
        });
      } else {
        if (!options?.hideErrorNotification) {
          showErrorNotification({
            title,
            message,
          });
        }
      }
    },
  });

  const mutate = useCallback(
    async (args: RouterInput[TType]): Promise<inferTrpcRouteOutput<TType>> => {
      if (showSuccessNotify) {
        showNotification({
          id: notifyId,
          loading: true,
          title: 'Loading...',
          message: 'We will update you shortly.',
          autoClose: false,
          radius: 10,
        });
      }

      const result = await mutateAsync(args);
      invalidate();
      return result;
    },
    [mutateAsync, invalidate, notifyId, showSuccessNotify]
  );

  return {
    mutate,
    loading,
    error,
  };
};
